import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GAME_WORDS } from '../../data/gameWords';
import { getDateKey } from '../WordMaster/wordMasterUtils';
import { useAuth } from '../../context/AuthContext';
import {
  getLetterHiveDailyPlaySnapshot,
  commitLetterHiveDailyResult,
  saveLetterHiveDailyPlay,
} from '../../firebase/letterHiveService';
import CozyPlayShell from '../../components/CozyPlayShell/CozyPlayShell';
import { HowToPlay } from './HowToPlay';
import {
  LETTER_HIVE_MIN_LENGTH,
  letterHiveDailySeed,
  pangramSourceWords,
  pickHiveFromCandidates,
  isValidHiveWord,
  totalScoreForFound,
  ringLetters,
  isPangram,
} from './letterHiveUtils';
import './LetterHive.css';

const HOW_TO_KEY = 'letter_hive_how_to_play_dismissed_v1';

function cloneHiveLetters(hive) {
  return {
    ...hive,
    letters: new Set(hive.letters),
  };
}

function containsWord(dict, w, minLen) {
  const x = w.trim().toUpperCase();
  if (x.length < minLen) return false;
  return dict.has(x);
}

export default function LetterHive() {
  const { user, refreshProfile } = useAuth();
  const [candidates, setCandidates] = useState(null);
  const [candidatesError, setCandidatesError] = useState(null);
  const [practice, setPractice] = useState(false);
  const [hive, setHive] = useState(null);
  const [found, setFound] = useState(() => new Set());
  const [score, setScore] = useState(0);
  const [mergedRemote, setMergedRemote] = useState(false);
  const [typing, setTyping] = useState('');
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [badMessage, setBadMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const initDoneRef = useRef(false);
  const dailySnapRef = useRef(null);
  const practiceSnapRef = useRef(null);
  const activeDailyBoardDateKeyRef = useRef(null);
  const persistTimerRef = useRef(null);
  const lastPersistAtRef = useRef(0);
  const lastCommitAtRef = useRef(0);
  const lastCommittedScoreRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    try {
      const list = pangramSourceWords(GAME_WORDS);
      if (!cancelled) {
        if (!list.length) {
          setCandidatesError('No hive candidates in dictionary.');
        } else {
          setCandidates(list);
        }
      }
    } catch (e) {
      if (!cancelled) setCandidatesError(e?.message || 'Could not build candidates');
    }
    return () => {
      cancelled = true;
    };
  }, []);

  const purgeStaleDailyHiveSnap = useCallback(() => {
    const today = getDateKey();
    const ds = dailySnapRef.current;
    if (ds?.dailyDateKey != null && ds.dailyDateKey !== today) {
      dailySnapRef.current = null;
    }
    if (
      activeDailyBoardDateKeyRef.current != null &&
      activeDailyBoardDateKeyRef.current !== today
    ) {
      activeDailyBoardDateKeyRef.current = null;
    }
  }, []);

  const hiveSnapStaleVsTodayCanonical = useCallback((snap, list) => {
    if (!snap || snap.dailyDateKey !== getDateKey()) return true;
    const canonical = pickHiveFromCandidates({
      sortedCandidates: list,
      seed: letterHiveDailySeed(),
      practice: false,
    });
    return canonical.hiveKey !== snap.hive.hiveKey;
  }, []);

  const resetHive = useCallback(
    async (list, isPractice, uid) => {
      if (!list?.length) return;
      if (isPractice) {
        practiceSnapRef.current = null;
      } else {
        dailySnapRef.current = null;
      }

      const seed = letterHiveDailySeed();
      const h = pickHiveFromCandidates({
        sortedCandidates: list,
        seed,
        practice: isPractice,
      });
      setMergedRemote(false);
      setHive(h);
      setFound(new Set());
      setScore(0);
      setTyping('');
      setBadMessage(null);
      setSaveError(null);
      activeDailyBoardDateKeyRef.current = isPractice ? null : getDateKey();

      if (!isPractice && uid) {
        const remote = await getLetterHiveDailyPlaySnapshot(uid);
        if (remote?.foundWords?.length && remote.hiveKey === h.hiveKey) {
          setFound(new Set(remote.foundWords));
          setScore(remote.totalScore);
          setMergedRemote(true);
        }
      }
    },
    []
  );

  const restoreHiveSnap = useCallback((snap) => {
    setHive(cloneHiveLetters(snap.hive));
    setFound(new Set(snap.found));
    setScore(snap.score);
    setMergedRemote(snap.mergedRemote);
    lastCommitAtRef.current = snap.lastCommitAt;
    lastCommittedScoreRef.current = snap.lastCommittedScore;
    setTyping(snap.typing);
    setBadMessage(snap.badMessage ?? null);
    setSaveError(snap.saveError ?? null);
    activeDailyBoardDateKeyRef.current = snap.dailyDateKey ?? null;
  }, []);

  const commitResultIfNeeded = useCallback(
    async (dateId, totalScore) => {
      if (practice || !user) return { ok: false, reason: 'noop' };
      const now = Date.now();
      // Commit profile/leaderboards at most once per minute, and only when score increased.
      if (totalScore <= lastCommittedScoreRef.current) return { ok: true, skipped: true };
      if (now - lastCommitAtRef.current < 60_000) return { ok: true, skipped: true };

      lastCommitAtRef.current = now;
      try {
        const res = await commitLetterHiveDailyResult({
          uid: user.uid,
          dateId,
          totalScore,
          displayName: user.displayName || user.email?.split('@')[0],
        });
        lastCommittedScoreRef.current = Math.max(lastCommittedScoreRef.current, totalScore);
        if (res?.ok) {
          // Refresh profile only after a server-side commit (not on every word).
          await refreshProfile();
        }
        return res;
      } catch (e) {
        return { ok: false, reason: e?.message || 'commit_failed' };
      }
    },
    [practice, refreshProfile, user]
  );

  const persistDailyNow = useCallback(
    async (nextFound, nextScore) => {
      if (practice || !user || !hive) return;
      setSaveError(null);
      const dateId = getDateKey();
      const words = [...nextFound].sort();
      try {
        await saveLetterHiveDailyPlay({
          uid: user.uid,
          dateId,
          foundWords: words,
          totalScore: nextScore,
          hiveKey: hive.hiveKey,
          displayName: user.displayName || user.email?.split('@')[0],
        });
        lastPersistAtRef.current = Date.now();
        await commitResultIfNeeded(dateId, nextScore);
      } catch (e) {
        setSaveError(e?.message || 'Could not save');
      }
    },
    [commitResultIfNeeded, hive, practice, user]
  );

  const schedulePersist = useCallback(
    (nextFound, nextScore) => {
      if (practice || !user || !hive) return;
      const now = Date.now();
      // Avoid saving more than once every 20s during active play.
      const dueIn = Math.max(0, 20_000 - (now - lastPersistAtRef.current));
      if (persistTimerRef.current) window.clearTimeout(persistTimerRef.current);
      persistTimerRef.current = window.setTimeout(() => {
        persistTimerRef.current = null;
        void persistDailyNow(nextFound, nextScore);
      }, dueIn);
    },
    [hive, persistDailyNow, practice, user]
  );

  const flushDailyPersistenceNow = useCallback(
    async (nextFound, nextScore) => {
      if (practice || !user || !hive) return;
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      await persistDailyNow(nextFound, nextScore);
    },
    [hive, persistDailyNow, practice, user]
  );

  const applyHiveModeTap = useCallback(
    async (nextPractice, list, uid) => {
      if (nextPractice === practice || !list?.length) return;

      if (!practice && nextPractice && user && hive) {
        await flushDailyPersistenceNow(found, score);
      }

      const cap = practice
        ? hive
          ? {
              hive: cloneHiveLetters(hive),
              found: [...found],
              score,
              mergedRemote,
              lastCommitAt: lastCommitAtRef.current,
              lastCommittedScore: lastCommittedScoreRef.current,
              typing,
              badMessage,
              saveError,
              dailyDateKey: null,
            }
          : null
        : hive
          ? {
              hive: cloneHiveLetters(hive),
              found: [...found],
              score,
              mergedRemote,
              lastCommitAt: lastCommitAtRef.current,
              lastCommittedScore: lastCommittedScoreRef.current,
              typing,
              badMessage,
              saveError,
              dailyDateKey: getDateKey(),
            }
          : null;

      if (practice && cap != null) {
        practiceSnapRef.current = cap;
      }
      if (!practice && cap != null) {
        dailySnapRef.current = cap;
      }

      setPractice(nextPractice);

      if (nextPractice) {
        const ps = practiceSnapRef.current;
        if (ps) restoreHiveSnap(ps);
        else await resetHive(list, true, uid ?? null);
      } else {
        purgeStaleDailyHiveSnap();
        const ds = dailySnapRef.current;
        if (ds != null && !hiveSnapStaleVsTodayCanonical(ds, list)) {
          restoreHiveSnap(ds);
        } else {
          dailySnapRef.current = null;
          await resetHive(list, false, uid ?? null);
        }
      }
    },
    [
      practice,
      user,
      hive,
      found,
      score,
      mergedRemote,
      typing,
      badMessage,
      saveError,
      flushDailyPersistenceNow,
      purgeStaleDailyHiveSnap,
      hiveSnapStaleVsTodayCanonical,
      resetHive,
      restoreHiveSnap,
    ]
  );

  useEffect(() => {
    if (!candidates?.length || initDoneRef.current) return;
    initDoneRef.current = true;
    void resetHive(candidates, false, user?.uid ?? null);
  }, [candidates, resetHive, user?.uid]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(HOW_TO_KEY) === '1') return;
    setShowHowToPlay(true);
  }, []);

  const handleHowToPlayClose = () => {
    setShowHowToPlay(false);
    try {
      window.localStorage.setItem(HOW_TO_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const ring = useMemo(() => (hive ? ringLetters(hive) : []), [hive]);

  useEffect(() => {
    const flush = () => {
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      if (!practice && user && hive) {
        void persistDailyNow(found, score);
      }
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        flush();
        return;
      }
      purgeStaleDailyHiveSnap();
      if (
        practice ||
        activeDailyBoardDateKeyRef.current == null ||
        activeDailyBoardDateKeyRef.current === getDateKey()
      ) {
        return;
      }
      if (candidates?.length) void resetHive(candidates, false, user?.uid ?? null);
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [
    candidates,
    found,
    hive,
    persistDailyNow,
    practice,
    purgeStaleDailyHiveSnap,
    resetHive,
    score,
    user,
  ]);

  const submit = () => {
    if (!hive) return;
    const w = typing.trim().toUpperCase();
    if (w.length < LETTER_HIVE_MIN_LENGTH) {
      setBadMessage(`Use at least ${LETTER_HIVE_MIN_LENGTH} letters`);
      return;
    }
    if (!isValidHiveWord(w, hive.letters, hive.center)) {
      setBadMessage('Must use hive letters and include the center');
      return;
    }
    if (!containsWord(GAME_WORDS, w, LETTER_HIVE_MIN_LENGTH)) {
      setBadMessage('Not in word list');
      return;
    }
    if (found.has(w)) {
      setBadMessage('Already found');
      return;
    }
    const next = new Set(found);
    next.add(w);
    const nextScore = totalScoreForFound(next, hive.letters, hive.center);
    setFound(next);
    setScore(nextScore);
    setBadMessage(null);
    setTyping('');
    if (!practice) {
      schedulePersist(next, nextScore);
    }
  };

  if (candidatesError) {
    return <p className="letter-hive__error">{candidatesError}</p>;
  }
  if (!candidates?.length || !hive) {
    return <p className="letter-hive__loading">Loading dictionary…</p>;
  }

  const sortedFound = [...found].sort();

  return (
    <CozyPlayShell
      className="letter-hive"
      title="Letter Hive"
      mode={practice ? 'practice' : 'daily'}
      onModeChange={(m) => void applyHiveModeTap(m === 'practice', candidates, user?.uid ?? null)}
      onHowToPlay={() => setShowHowToPlay(true)}
      subtitle="Find words using the hive letters"
      meta={
        <>
          <span className="letter-hive__score">Score: {score}</span>
          {!practice && <span className="letter-hive__date">{getDateKey()} (local)</span>}
        </>
      }
    >
      {showHowToPlay && <HowToPlay onClose={handleHowToPlayClose} />}

      <div className="letter-hive__hive-wrap">
        <div className="letter-hive__hive-row">
          {ring[0] && <span className="letter-hive__cell letter-hive__cell--ring">{ring[0]}</span>}
          {ring[1] && <span className="letter-hive__cell letter-hive__cell--ring">{ring[1]}</span>}
        </div>
        <div className="letter-hive__hive-row">
          {ring[2] && <span className="letter-hive__cell letter-hive__cell--ring">{ring[2]}</span>}
          <span className="letter-hive__cell letter-hive__cell--center">{hive.center}</span>
          {ring[3] && <span className="letter-hive__cell letter-hive__cell--ring">{ring[3]}</span>}
        </div>
        <div className="letter-hive__hive-row">
          {ring[4] && <span className="letter-hive__cell letter-hive__cell--ring">{ring[4]}</span>}
          {ring[5] && <span className="letter-hive__cell letter-hive__cell--ring">{ring[5]}</span>}
        </div>
      </div>
      <p className="letter-hive__hint">Center letter must appear in every word</p>

      <input
        className="letter-hive__input"
        value={typing}
        onChange={(e) => {
          setTyping(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''));
          if (badMessage) setBadMessage(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        placeholder="Type a word"
        autoCapitalize="characters"
        spellCheck={false}
        aria-label="Word entry"
      />
      <div className="letter-hive__error">{badMessage || saveError || ''}</div>

      <div className="letter-hive__actions">
        <button type="button" className="letter-hive__btn letter-hive__btn--primary" onClick={submit}>
          Submit
        </button>
        <button
          type="button"
          className="letter-hive__btn letter-hive__btn--secondary"
          onClick={() => void resetHive(candidates, practice, user?.uid ?? null)}
        >
          {practice ? 'New hive' : 'Reset'}
        </button>
      </div>

      <div className="letter-hive__found">
        <h3>Found ({found.size})</h3>
        <div className="letter-hive__found-chips">
          {sortedFound.length ? (
            sortedFound.map((w) => (
              <span
                key={w}
                className={`letter-hive__word-chip${
                  isPangram(w, hive.letters) ? ' letter-hive__word-chip--pangram' : ''
                }`}
              >
                {w}
              </span>
            ))
          ) : (
            <span className="letter-hive__found-empty">—</span>
          )}
        </div>
      </div>
    </CozyPlayShell>
  );
}
